import type { DemandLevel } from "../../types/api";

/**
 * The welcome page's product preview: one week of an example property's
 * prices, each with the explanation the product prints beside a price.
 *
 * It is a worked example, not the engine — three room types, a weekday
 * adjustment, a demand reading, the weather and the competitors' prices,
 * chosen so every amount is a whole number and every price lands on a round
 * ten. What it shares with the product is the promise it demonstrates: the
 * explanation adds up to the price.
 */

export type PreviewRoomKey = "double" | "family" | "suite";

export interface PreviewRoom {
    key: PreviewRoomKey;
    basePrice: number;
}

/**
 * Whole thousands: every line is a whole percentage of the base price, and a
 * percent of a thousand is ten, so no amount needs rounding and none could
 * make the explanation miss its price by a crown.
 */
export const PREVIEW_ROOMS: PreviewRoom[] = [
    { key: "double", basePrice: 2000 },
    { key: "family", basePrice: 3000 },
    { key: "suite", basePrice: 4000 },
];

/** Weekday adjustment in percent, by `Date.getDay()` (0 = Sunday). */
const WEEKDAY_PCT: Record<number, number> = { 0: -5, 1: 0, 2: 0, 3: 0, 4: 0, 5: 10, 6: 15 };

/** How the example week is selling, by `Date.getDay()`. */
const DEMAND_BY_WEEKDAY: Record<number, DemandLevel> = {
    0: "low",
    1: "low",
    2: "minimal",
    3: "standard",
    4: "standard",
    5: "elevated",
    6: "high",
};

/** What each demand level moves the price by, in percent of the base. */
const DEMAND_PCT: Record<DemandLevel, number> = {
    minimal: -10,
    low: -5,
    standard: 0,
    elevated: 5,
    high: 10,
};

/** What the forecast says about a night; `neutral` moves nothing. */
export type PreviewWeather = "sunny" | "neutral" | "rainy";

/** The example week's forecast: a sunny weekend and a wet Tuesday. */
const WEATHER_BY_WEEKDAY: Record<number, PreviewWeather> = {
    0: "neutral",
    1: "neutral",
    2: "rainy",
    3: "neutral",
    4: "neutral",
    5: "sunny",
    6: "sunny",
};

export const PREVIEW_WEATHER_PCT: Record<PreviewWeather, number> = {
    sunny: 2,
    neutral: 0,
    rainy: -2,
};

/** Where the competitors' prices stand against the example property's; `level` moves nothing. */
export type PreviewCompetitors = "cheaper" | "level" | "pricier";

/** The example week's market: cheaper than us on the busy Saturday, dearer on Monday. */
const COMPETITORS_BY_WEEKDAY: Record<number, PreviewCompetitors> = {
    0: "level",
    1: "pricier",
    2: "level",
    3: "level",
    4: "level",
    5: "level",
    6: "cheaper",
};

export const PREVIEW_COMPETITORS_PCT: Record<PreviewCompetitors, number> = {
    cheaper: -3,
    level: 0,
    pricier: 3,
};

export interface PreviewNight {
    date: Date;
    demand: DemandLevel;
    weather: PreviewWeather;
    competitors: PreviewCompetitors;
}

const THURSDAY = 4;

/**
 * Seven nights from the coming Thursday — today, if today is one — so the
 * weekend sits in the first four columns, which is all a phone shows, and the
 * week shown is always the one ahead of the day the page is opened.
 * Local calendar days, like every other date the UI prints.
 */
export function previewNights(today: Date): PreviewNight[] {
    const offset = (THURSDAY - today.getDay() + 7) % 7;
    return Array.from({ length: 7 }, (_, i) => {
        const date = new Date(today.getFullYear(), today.getMonth(), today.getDate() + offset + i);
        const weekday = date.getDay();
        return {
            date,
            demand: DEMAND_BY_WEEKDAY[weekday],
            weather: WEATHER_BY_WEEKDAY[weekday],
            competitors: COMPETITORS_BY_WEEKDAY[weekday],
        };
    });
}

export interface PreviewQuote {
    base: number;
    weekdayPct: number;
    weekdayAmount: number;
    demandPct: number;
    demandAmount: number;
    weatherPct: number;
    weatherAmount: number;
    competitorPct: number;
    competitorAmount: number;
    price: number;
}

/** One cell's price and the lines that explain it. */
export function quotePreviewNight(room: PreviewRoom, night: PreviewNight): PreviewQuote {
    const base = room.basePrice;
    const weekdayPct = WEEKDAY_PCT[night.date.getDay()];
    const demandPct = DEMAND_PCT[night.demand];
    const weatherPct = PREVIEW_WEATHER_PCT[night.weather];
    const competitorPct = PREVIEW_COMPETITORS_PCT[night.competitors];
    const weekdayAmount = (base * weekdayPct) / 100;
    const demandAmount = (base * demandPct) / 100;
    const weatherAmount = (base * weatherPct) / 100;
    const competitorAmount = (base * competitorPct) / 100;
    return {
        base,
        weekdayPct,
        weekdayAmount,
        demandPct,
        demandAmount,
        weatherPct,
        weatherAmount,
        competitorPct,
        competitorAmount,
        price: base + weekdayAmount + demandAmount + weatherAmount + competitorAmount,
    };
}
